import React, { useState, useEffect } from 'react';
import { Form, Checkbox, TimePicker, Button, Card, Row, Col, Divider, message, Tabs, Select, InputNumber, Input } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, ApartmentOutlined, EnvironmentOutlined, SettingOutlined, FormOutlined, QrcodeOutlined, GlobalOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import PageHeader from '@iso/components/utility/pageHeader';
import { propertySettingsService, corporateGroupService, propertyService } from '@iso/services';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PropertySettingsWrapper from './PropertySettings.styles';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import DivisionsManagement from './DivisionsManagement';
import DivisionSitesManagement from './DivisionSitesManagement';
import CheckInFormsManagement from './CheckInFormsManagement';
import InductionRequirementsManagement from './InductionRequirementsManagement';
import PropertyQrPanel from './PropertyQrPanel';
import MapDisplaySettings from './MapDisplaySettings';
import CorporateGroupPinModal from './CorporateGroupPinModal';

const { Option } = Select;

// Main office contacts (contractor contact escalations): the backend caps the list.
const MAX_MAIN_OFFICE_CONTACTS = 10;
const userFullName = (u) => `${u.name || ''} ${u.surname || ''}`.trim() || u.email || `User #${u.id}`;

const PropertySettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const currentProperty = useSelector((state) => state.property.currentProperty);
  // Deciding what a property MANDATES is a management call, unlike running an
  // induction, which any member does at the gate. The API enforces the same
  // rule; this just keeps the tab out of the way of people who cannot use it.
  const activePermission = useSelector((state) => state.permission.activePermission);
  const canManageProperty = activePermission === 'owner' || activePermission === 'admin';
  const [property, setProperty] = useState(null);
  const [corporateGroups, setCorporateGroups] = useState([]);
  // Users of this property and of the other properties in its corporate group,
  // offered when picking a main office contact.
  const [propertyUsers, setPropertyUsers] = useState([]);
  // The corporate_group_id last persisted to the server. Used to detect
  // whether the dropdown has actually changed on submit so we only prompt
  // for the PIN on transitions to a different group.
  const [savedCorporateGroupId, setSavedCorporateGroupId] = useState(null);
  // PIN prompt state — populated when submit detects a protected group
  // change. pendingFormValues is held so we can resume submit after the
  // PIN is verified by the backend.
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pinTargetGroup, setPinTargetGroup] = useState(null);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  useEffect(() => {
    // Get property from Redux or localStorage
    const storedProperty = localStorage.getItem('active_property');
    const activeProperty = currentProperty || (storedProperty ? JSON.parse(storedProperty) : null);

    if (activeProperty && activeProperty.id) {
      setProperty(activeProperty);
      loadSettings(activeProperty.id);
      loadCorporateGroups();
      propertyService.getListUsersOfProperty(activeProperty.id, false, [], { includeCorporateGroup: true })
        .then((res) => {
          if (res && res.code === '0000') {
            setPropertyUsers(res.users || []);
          }
        })
        .catch((error) => console.error('Error loading property users:', error));
    } else {
      setInitialLoading(false);
      message.error('No property selected. Please select a property first.');
    }
  }, [currentProperty]);

  const loadCorporateGroups = async () => {
    try {
      const response = await corporateGroupService.getList();
      if (response && response.code === '0000') {
        setCorporateGroups(response.corporate_groups || []);
      }
    } catch (error) {
      console.error('Error loading corporate groups:', error);
    }
  };

  const loadSettings = async (propertyId) => {
    try {
      setInitialLoading(true);
      const response = await propertySettingsService.getSettings(propertyId);

      if (response.code === '0000' && response.settings) {
        const { settings } = response;

        // Set business days
        const businessDaysValues = settings.business_days || {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        };

        // Set working hours - Convert from UTC to local time for display
        // Create a date with the UTC time, then convert to local
        const workingHoursStart = settings.working_hours_start
          ? moment.utc(`2000-01-01 ${settings.working_hours_start}`, 'YYYY-MM-DD HH:mm:ss').local()
          : moment('07:00:00', 'HH:mm:ss');

        const workingHoursEnd = settings.working_hours_end
          ? moment.utc(`2000-01-01 ${settings.working_hours_end}`, 'YYYY-MM-DD HH:mm:ss').local()
          : moment('15:00:00', 'HH:mm:ss');

        form.setFieldsValue({
          corporate_group_id: settings.corporate_group_id || null,
          business_days: businessDaysValues,
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd,
          main_office_contacts: (settings.main_office_contacts || []).map((c) => ({
            user_id: c.user_id ? Number(c.user_id) : undefined,
            name: c.name || '',
            phone: c.phone || '',
          })),
          division_contact_timeout_minutes: settings.division_contact_timeout_minutes || 15,
        });

        setSavedCorporateGroupId(settings.corporate_group_id || null);

        // Update property object with corporate_group_id
        setProperty(prevProperty => {
          if (prevProperty) {
            return {
              ...prevProperty,
              corporate_group_id: settings.corporate_group_id || null
            };
          }
          return prevProperty;
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load property settings');
    } finally {
      setInitialLoading(false);
    }
  };

  const submitSettings = async (values, pin = null) => {
    try {
      setLoading(true);

      // Convert working hours from local time to UTC before saving
      // Create a date with local time, convert to UTC, then extract time only
      const startLocalDateTime = moment().hours(values.working_hours_start.hours()).minutes(values.working_hours_start.minutes()).seconds(0);
      const endLocalDateTime = moment().hours(values.working_hours_end.hours()).minutes(values.working_hours_end.minutes()).seconds(0);

      const data = {
        corporate_group_id: values.corporate_group_id || null,
        business_days: values.business_days,
        working_hours_start: startLocalDateTime.utc().format('HH:mm:ss'),
        working_hours_end: endLocalDateTime.utc().format('HH:mm:ss'),
        main_office_contacts: (values.main_office_contacts || []).map((c) => ({
          user_id: c.user_id || null,
          name: (c.name || '').trim(),
          phone: (c.phone || '').trim() || null,
        })),
        division_contact_timeout_minutes: values.division_contact_timeout_minutes || 15,
      };
      if (pin) {
        data.corporate_group_pin = pin;
      }

      const response = await propertySettingsService.updateSettings(property.id, data);

      if (response.code === '0000') {
        message.success('Property settings updated successfully');
        setProperty({
          ...property,
          corporate_group_id: values.corporate_group_id || null
        });
        setSavedCorporateGroupId(values.corporate_group_id || null);
        setPinModalVisible(false);
        setPinTargetGroup(null);
        setPendingFormValues(null);
        await loadSettings(property.id);
        return true;
      }

      // Backend rejected — surface the message. If it was a PIN error,
      // keep the modal open so the user can retry without re-entering the
      // form.
      if (response.requires_pin) {
        message.error(response.message || 'Incorrect PIN');
      } else {
        message.error(response.message || 'Failed to update settings');
      }
      return false;
    } catch (error) {
      console.error('Error updating settings:', error);
      message.error('Failed to update property settings');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Picking a user fills that contact's name and phone; both stay editable, and
  // clearing the user leaves whatever was typed.
  const handleMainOfficeUserChange = (index, userId) => {
    const user = propertyUsers.find((u) => Number(u.id) === Number(userId));
    if (!user) {
      return;
    }
    const contacts = form.getFieldValue('main_office_contacts') || [];
    form.setFieldsValue({
      main_office_contacts: contacts.map((c, i) => (i === index
        ? { ...c, name: userFullName(user), ...(user.mobile_phone ? { phone: user.mobile_phone } : {}) }
        : c)),
    });
  };

  // The same person can come back once per group property; list them once.
  const mainOfficeUserOptions = Object.values(
    propertyUsers.reduce((acc, u) => {
      const id = Number(u.id);
      if (!acc[id]) {
        const otherProperty = u.property_name && property && u.property_name !== property.name;
        acc[id] = { value: id, label: otherProperty ? `${userFullName(u)} — ${u.property_name}` : userFullName(u) };
      }
      return acc;
    }, {})
  );

  const handleSubmit = async (values) => {
    if (!property || !property.id) {
      message.error('No property selected');
      return;
    }

    const businessDays = values.business_days || {};
    const hasAtLeastOneDay = Object.values(businessDays).some(day => day === true);

    if (!hasAtLeastOneDay) {
      message.error('Please select at least one business day');
      return;
    }

    const targetGroupId = values.corporate_group_id || null;
    const isChanging = targetGroupId !== savedCorporateGroupId;

    // Only the assignment direction needs a PIN. Clearing or leaving the
    // value alone does not expose any cross-property data, so we skip the
    // prompt for those.
    if (targetGroupId && isChanging) {
      const targetGroup = corporateGroups.find(g => g.id === targetGroupId);
      if (targetGroup && targetGroup.has_pin) {
        setPinTargetGroup(targetGroup);
        setPendingFormValues(values);
        setPinModalVisible(true);
        return;
      }
    }

    await submitSettings(values);
  };

  const handlePinConfirm = async (pin) => {
    if (!pendingFormValues) return;
    await submitSettings(pendingFormValues, pin);
  };

  const handlePinCancel = () => {
    setPinModalVisible(false);
    setPinTargetGroup(null);
    setPendingFormValues(null);
  };

  if (initialLoading) {
    return (
      <LayoutWrapper>
        <PageHeader>Property Settings</PageHeader>
        <Box>
          <Card loading={true} />
        </Box>
      </LayoutWrapper>
    );
  }

  if (!property) {
    return (
      <LayoutWrapper>
        <PageHeader>Property Settings</PageHeader>
        <Box>
          <Card>
            <p>No property selected. Please select a property from the properties list.</p>
          </Card>
        </Box>
      </LayoutWrapper>
    );
  }

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          General Settings
        </span>
      ),
      children: (
        <Card className="settings-card" title="Business Days & Working Hours Configuration">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                corporate_group_id: null,
                business_days: {
                  monday: true,
                  tuesday: true,
                  wednesday: true,
                  thursday: true,
                  friday: true,
                  saturday: false,
                  sunday: false,
                },
                working_hours_start: moment('07:00:00', 'HH:mm:ss'),
                working_hours_end: moment('15:00:00', 'HH:mm:ss'),
                main_office_contacts: [],
                division_contact_timeout_minutes: 15,
              }}
            >
              {/* Corporate Group Section */}
              <Divider orientation="left">Corporate Group</Divider>
              <p className="section-description">
                Select the corporate group for this property. Divisions and sites will be synchronized across all properties in the same corporate group.
              </p>

              <Form.Item
                label="Corporate Group"
                name="corporate_group_id"
              >
                <Select
                  placeholder="Select a corporate group"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {corporateGroups.map(group => (
                    <Option key={group.id} value={group.id}>
                      {group.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Business Days Section */}
              <Divider orientation="left">Business Days</Divider>
              <p className="section-description">
                Select which days are considered business days for this property.
                These days will be used to calculate deadlines for hazard and incident reporting.
              </p>

              <div className="business-days-container">
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'monday']} valuePropName="checked" noStyle>
                      <Checkbox>Monday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'tuesday']} valuePropName="checked" noStyle>
                      <Checkbox>Tuesday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'wednesday']} valuePropName="checked" noStyle>
                      <Checkbox>Wednesday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'thursday']} valuePropName="checked" noStyle>
                      <Checkbox>Thursday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'friday']} valuePropName="checked" noStyle>
                      <Checkbox>Friday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'saturday']} valuePropName="checked" noStyle>
                      <Checkbox>Saturday</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['business_days', 'sunday']} valuePropName="checked" noStyle>
                      <Checkbox>Sunday</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Working Hours Section */}
              <Divider orientation="left">Working Hours</Divider>
              <p className="section-description">
                Set the standard working hours for this property.
                Deadlines will be calculated based on these hours.
              </p>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Working Hours Start"
                    name="working_hours_start"
                    rules={[{ required: true, message: 'Please select start time' }]}
                  >
                    <TimePicker
                      format="HH:mm"
                      style={{ width: '100%' }}
                      placeholder="Select start time"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Working Hours End"
                    name="working_hours_end"
                    rules={[
                      { required: true, message: 'Please select end time' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const start = getFieldValue('working_hours_start');
                          if (!value || !start || value.isAfter(start)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('End time must be after start time'));
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      format="HH:mm"
                      style={{ width: '100%' }}
                      placeholder="Select end time"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Contractor contact escalation */}
              <Divider orientation="left">Contractor contact</Divider>
              <p className="section-description">
                The contractor is sent to these people if they can't reach their division contact. They get an SMS (to the phone here) and can confirm the contractor has reported in.
                {!canManageProperty && ' Only owners and admins can change these.'}
              </p>

              <Form.List name="main_office_contacts">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Row gutter={16} key={field.key} align="bottom">
                        <Col xs={24} md={9}>
                          <Form.Item label="User" name={[field.name, 'user_id']}>
                            <Select
                              showSearch
                              allowClear
                              placeholder="Pick a user to fill name and phone"
                              optionFilterProp="label"
                              options={mainOfficeUserOptions}
                              onChange={(value) => handleMainOfficeUserChange(index, value)}
                              disabled={!canManageProperty}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={7}>
                          <Form.Item
                            label="Name"
                            name={[field.name, 'name']}
                            rules={[
                              { required: true, whitespace: true, message: 'Please enter a name' },
                              { max: 255, message: 'Name must be 255 characters or fewer' },
                            ]}
                          >
                            <Input placeholder="Name" disabled={!canManageProperty} />
                          </Form.Item>
                        </Col>
                        <Col xs={20} md={6}>
                          <Form.Item
                            label="Phone"
                            name={[field.name, 'phone']}
                            rules={[{ max: 50, message: 'Phone must be 50 characters or fewer' }]}
                          >
                            <Input placeholder="Mobile for SMS" disabled={!canManageProperty} />
                          </Form.Item>
                        </Col>
                        <Col xs={4} md={2}>
                          <Form.Item>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              aria-label={`Remove main office contact ${index + 1}`}
                              onClick={() => remove(field.name)}
                              disabled={!canManageProperty}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                    <Form.Item label={fields.length === 0 ? 'Main office contacts' : undefined}>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add({ user_id: undefined, name: '', phone: '' })}
                        disabled={!canManageProperty || fields.length >= MAX_MAIN_OFFICE_CONTACTS}
                      >
                        Add contact
                      </Button>
                      {fields.length >= MAX_MAIN_OFFICE_CONTACTS && (
                        <span style={{ marginLeft: 8, color: '#666' }}>Up to {MAX_MAIN_OFFICE_CONTACTS} contacts.</span>
                      )}
                    </Form.Item>
                  </>
                )}
              </Form.List>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Escalate if contact isn't confirmed within (minutes)"
                    name="division_contact_timeout_minutes"
                    rules={[
                      { required: true, message: 'Please enter a timeout' },
                      { type: 'integer', min: 1, max: 240, message: 'Enter a whole number from 1 to 240' },
                    ]}
                  >
                    <InputNumber min={1} max={240} precision={0} style={{ width: '100%' }} disabled={!canManageProperty} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Submit Button */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  className="save-button"
                  style={{ backgroundColor: '#0b2a1b', borderColor: '#0b2a1b' }}
                >
                  Save Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        ),
      },
      {
        key: 'divisions',
        label: (
          <span>
            <ApartmentOutlined />
            Divisions
          </span>
        ),
        children: (
          <Card className="settings-card" title="Divisions Management">
            <p style={{ marginBottom: 16, color: '#666' }}>
              The work areas contractors choose when they sign in (e.g. Maintenance, Irrigation, Site Management, Stock pad &amp; Processing). Give each area a responsible person at this property: contractors see that person and where to meet them, must make contact before starting work, and the person is notified when someone signs in for their area.
            </p>
            <DivisionsManagement propertyId={property?.id} corporateGroupId={property?.corporate_group_id} canManage={canManageProperty} />
          </Card>
        ),
      },
      {
        key: 'division-sites',
        label: (
          <span>
            <EnvironmentOutlined />
            Division Sites
          </span>
        ),
        children: (
          <Card className="settings-card" title="Division Sites Management">
            <p style={{ marginBottom: 16, color: '#666' }}>
              Assign property addresses to divisions. Each division can be linked to multiple properties within the same corporate group, allowing you to specify which locations belong to each division.
            </p>
            <DivisionSitesManagement corporateGroupId={property?.corporate_group_id} />
          </Card>
        ),
      },
      {
        key: 'check-in-forms',
        label: (
          <span>
            <FormOutlined />
            Property Forms
          </span>
        ),
        children: (
          <Card className="settings-card" title="Property Forms">
            <CheckInFormsManagement propertyId={property?.id} />
          </Card>
        ),
      },
      ...(canManageProperty
        ? [{
            key: 'inductions',
            label: (
              <span>
                <SafetyCertificateOutlined />
                Inductions
              </span>
            ),
            children: (
              <Card className="settings-card" title="Required Inductions">
                <InductionRequirementsManagement propertyId={property?.id} />
              </Card>
            ),
          }]
        : []),
      {
        key: 'map-display',
        label: (
          <span>
            <GlobalOutlined />
            Map Display
          </span>
        ),
        children: (
          <Card className="settings-card" title="Map Display">
            <MapDisplaySettings propertyId={property?.id} />
          </Card>
        ),
      },
      {
        key: 'property-qr',
        label: (
          <span>
            <QrcodeOutlined />
            Property QR Code
          </span>
        ),
        children: (
          <Card className="settings-card" title="Property QR Code">
            <PropertyQrPanel propertyId={property?.id} />
          </Card>
        ),
      },
    ];

  return (
    <PropertySettingsWrapper>
      <LayoutWrapper>
        <Box>
          <div className="page-header-wrapper">
            <h2 className="page-title">Property Settings - {property.name}</h2>
          </div>

          <Tabs
            defaultActiveKey="general"
            items={tabItems}
            size="large"
            style={{ marginTop: 16 }}
          />
        </Box>
      </LayoutWrapper>
      <CorporateGroupPinModal
        visible={pinModalVisible}
        groupName={pinTargetGroup?.name}
        confirmLoading={loading}
        onCancel={handlePinCancel}
        onConfirm={handlePinConfirm}
      />
    </PropertySettingsWrapper>
  );
};

export default PropertySettings;
